import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { z } from "zod";

import {
  formatAuthError,
  isEmailVerificationError,
  useDevLoginSkipVerification,
  useLogin,
} from "@/features/auth/model/useAuth";
import { cn } from "@/shared/lib/cn";
import { Button, FormHint, Input, Label } from "@/shared/ui";

const schema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z.string().min(1, "Enter your password"),
});

type FormValues = z.infer<typeof schema>;

const GOOGLE_SSO_ENABLED = import.meta.env.VITE_GOOGLE_SSO_ENABLED === "true";

export function LoginForm() {
  const login = useLogin();
  const devSkip = useDevLoginSkipVerification();
  const [showPassword, setShowPassword] = useState(false);
  const [showSlowHint, setShowSlowHint] = useState(false);
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = handleSubmit((values) => login.mutate(values));

  // Surface a "still working…" hint after 3s so a slow B1/cold-bcrypt response
  // doesn't read as a hung UI. Resets when the request finishes.
  useEffect(() => {
    if (!login.isPending) {
      setShowSlowHint(false);
      return;
    }
    const timer = window.setTimeout(() => setShowSlowHint(true), 3000);
    return () => window.clearTimeout(timer);
  }, [login.isPending]);

  const needsVerification = isEmailVerificationError(login.error);

  const handleGoogleLogin = () => {
    window.location.href = "/api/v1/auth/google/login?intent=login";
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {GOOGLE_SSO_ENABLED && (
        <div className="space-y-3">
          <Button
            type="button"
            variant="outline"
            size="lg"
            className="w-full"
            onClick={handleGoogleLogin}
          >
            Continue with Google
          </Button>
          <div className="relative flex items-center" aria-hidden>
            <span className="h-px flex-1 bg-border" />
            <span className="px-3 text-xs uppercase tracking-wide text-copy-muted">or</span>
            <span className="h-px flex-1 bg-border" />
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          invalid={!!errors.email}
          {...register("email")}
        />
        {errors.email && <FormHint error>{errors.email.message}</FormHint>}
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label htmlFor="password">Password</Label>
          <Link
            to="/forgot-password"
            className="text-xs font-medium text-primary hover:underline"
          >
            Forgot password?
          </Link>
        </div>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            invalid={!!errors.password}
            className="pr-10"
            {...register("password")}
          />
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            aria-label={showPassword ? "Hide password" : "Show password"}
            aria-pressed={showPassword}
            className={cn(
              "absolute inset-y-0 right-0 flex items-center px-3 text-copy-muted transition-colors",
              "hover:text-copy focus-visible:outline-none focus-visible:text-copy",
            )}
            tabIndex={-1}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {errors.password && <FormHint error>{errors.password.message}</FormHint>}
      </div>

      {login.error && !needsVerification && (
        <div className="rounded-md border border-destructive bg-destructive-light px-3 py-2 text-sm text-destructive-foreground">
          {formatAuthError(login.error)}
        </div>
      )}

      {needsVerification && (
        <div className="space-y-2 rounded-md border border-warning bg-warning-light px-3 py-2.5 text-sm text-warning-foreground">
          <p>{formatAuthError(login.error)}</p>
          <button
            type="button"
            onClick={() => alert("Resend verification — coming soon")}
            className="text-sm font-medium underline-offset-4 hover:underline"
          >
            Resend verification email
          </button>
          {import.meta.env.DEV && (
            <div className="border-t border-warning/40 pt-2">
              <p className="mb-1.5 text-xs">
                Demo mode: skip verification and sign in anyway.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={devSkip.isPending}
                onClick={() => devSkip.mutate(getValues())}
              >
                {devSkip.isPending ? "Signing in…" : "Skip verification (demo)"}
              </Button>
              {devSkip.error && (
                <p className="mt-1.5 text-xs text-destructive-foreground">
                  {formatAuthError(devSkip.error)}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <Button type="submit" size="lg" className="w-full" disabled={isSubmitting || login.isPending}>
        {login.isPending ? "Signing in…" : "Sign in"}
      </Button>

      {showSlowHint && (
        <p className="text-center text-xs text-copy-muted">
          Still working — the server can be slow to wake up. Hang tight.
        </p>
      )}

      <p className="text-center text-sm text-copy-muted">
        No account?{" "}
        <Link to="/signup" className="font-medium text-primary hover:underline">
          Create one
        </Link>
      </p>
    </form>
  );
}
